import React, { Dispatch, SetStateAction } from "react";

export type KeyArgs<TGroup, TDetail> = {
  manualMode: boolean;
  open: boolean;
  level: "groups" | "details";
  groups: TGroup[];
  details: TDetail[];
  highlight: number;
  setHighlight: Dispatch<SetStateAction<number>>;
  setOpen: Dispatch<SetStateAction<boolean>>;
  setLevel: Dispatch<SetStateAction<"groups" | "details">>;
  setDetails: Dispatch<SetStateAction<TDetail[]>>;
  drillInto: (group: TGroup) => void;
  selectDetail: (detail: TDetail) => void;
};
/**
 * Handles ArrowUp/Down, Enter, Escape, ArrowLeft for the suggestions listbox.
 * Keeps logic pure and testable; caller provides state and setters.
 *
 * Prevents default for selection/navigation keys and delegates to provided
 * `drillInto` (for groups) or `selectDetail` (for details).
 */
export function handleListKey<TGroup, TDetail>(
  evt: React.KeyboardEvent<HTMLInputElement>,
  args: KeyArgs<TGroup, TDetail>
) {
  const {
    manualMode, open, level, groups, details, highlight,
    setHighlight, setOpen, setLevel, setDetails, drillInto, selectDetail,
  } = args;

  if (manualMode || !open) return;

  const isGroups = level === "groups";
  const len = isGroups ? groups.length : details.length;

  switch (evt.key) {
    case "ArrowDown":
      if (!len) return;
      evt.preventDefault();
      setHighlight(prev => (prev + 1) % len);
      break;
    case "ArrowUp":
      if (!len) return;
      evt.preventDefault();
      setHighlight(prev => (prev - 1 + len) % len);
      break;
    case "Enter":
      if (!(highlight >= 0 && len > 0)) return;
      evt.preventDefault();
      if (isGroups) {
        const group = groups[highlight];
        if (group) drillInto(group);
      } else {
        const detail = details[highlight];
        if (detail) selectDetail(detail);
      }
      break;
    case "Escape":
      setOpen(false);
      break;
    case "ArrowLeft":
      if (!isGroups) {
        evt.preventDefault();
        setLevel("groups");
        setDetails([]);
        setHighlight(groups.length ? 0 : -1);
      }
      break;
  }
}
